import memoize from "@-/com.npmjs.memoize";
import type { ChinaMapPlace } from "@-/dev.improve.haniverse.data.china-map";

/** A closed outline as a flat `[x0, y0, x1, y1, …]` list in map units. */
export type ChinaMapRing = Float64Array;

/**
 * Turns a `china-map.gen.json` outline back into rings for hit-testing. The generator only emits
 * `M x y` (absolute move), `l dx dy` (relative line) and `z` (close), so that is all this reads.
 */
export function parseChinaMapPath(path: string): ChinaMapRing[] {
  const rings: ChinaMapRing[] = [];
  let ring: number[] = [];
  let command = "";
  let x = 0;
  let y = 0;
  const numbers: number[] = [];
  for (const [token] of path.matchAll(/[Mlz]|-?(?:\d+\.?\d*|\.\d+)/g)) {
    if (token === "M" || token === "l") {
      command = token;
      continue;
    }
    if (token === "z") {
      if (ring.length >= 6) {
        rings.push(Float64Array.from(ring));
      }
      ring = [];
      continue;
    }
    numbers.push(Number(token));
    if (numbers.length < 2) {
      continue;
    }
    const [first = 0, second = 0] = numbers;
    numbers.length = 0;
    if (command === "M") {
      x = first;
      y = second;
    } else {
      x += first;
      y += second;
    }
    ring.push(x, y);
  }
  return rings;
}

/** Outlines are immutable: hit testing and label placement share one parse (and its index). */
export const getChinaMapPlaceRings = memoize(
  (place: ChinaMapPlace): ChinaMapRing[] => parseChinaMapPath(place.path),
  { cache: new WeakMap() },
);

/**
 * Even–odd point-in-polygon over every ring of a region, so holes (an enclave drawn as a second
 * ring) and multi-part regions (islands) both resolve correctly.
 */
export function isPointInChinaMapRings(
  rings: readonly ChinaMapRing[],
  x: number,
  y: number,
): boolean {
  let inside = false;
  for (const ring of rings) {
    for (
      let index = 0, previous = ring.length - 2;
      index < ring.length;
      previous = index, index += 2
    ) {
      const xi = ring[index] ?? 0;
      const yi = ring[index + 1] ?? 0;
      const xj = ring[previous] ?? 0;
      const yj = ring[previous + 1] ?? 0;
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
  }
  return inside;
}

export function isPointInChinaMapBbox(
  [minX, minY, maxX, maxY]: readonly [number, number, number, number],
  x: number,
  y: number,
): boolean {
  return x >= minX && x <= maxX && y >= minY && y <= maxY;
}

export type ChinaMapBbox = readonly [number, number, number, number];
export type ChinaMapPoint = readonly [number, number];

/** Inclusive, so a glyph or leader touching an outline is treated as a collision. */
export function doChinaMapBboxesIntersect(a: ChinaMapBbox, b: ChinaMapBbox): boolean {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];
}

/** Clips an entire segment, rather than sampling points that can miss a thin island or notch. */
export function doesChinaMapSegmentIntersectBbox(
  start: ChinaMapPoint,
  end: ChinaMapPoint,
  box: ChinaMapBbox,
): boolean {
  let enter = 0;
  let exit = 1;
  for (const [origin, delta, min, max] of [
    [start[0], end[0] - start[0], box[0], box[2]],
    [start[1], end[1] - start[1], box[1], box[3]],
  ] as const) {
    if (delta === 0) {
      if (origin < min || origin > max) {
        return false;
      }
    } else {
      const first = (min - origin) / delta;
      const last = (max - origin) / delta;
      enter = Math.max(enter, Math.min(first, last));
      exit = Math.min(exit, Math.max(first, last));
    }
  }
  return enter <= exit;
}

export function doChinaMapSegmentsIntersect(
  a: ChinaMapPoint,
  b: ChinaMapPoint,
  c: ChinaMapPoint,
  d: ChinaMapPoint,
): boolean {
  if (
    !doChinaMapBboxesIntersect(
      [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])],
      [Math.min(c[0], d[0]), Math.min(c[1], d[1]), Math.max(c[0], d[0]), Math.max(c[1], d[1])],
    )
  ) {
    return false;
  }
  const cross = (p: ChinaMapPoint, q: ChinaMapPoint, r: ChinaMapPoint) =>
    (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  return cross(a, b, c) * cross(a, b, d) <= 0 && cross(c, d, a) * cross(c, d, b) <= 0;
}

/** All outline edges, including island boundaries and holes. */
export function* getChinaMapRingEdges(rings: readonly ChinaMapRing[]) {
  for (const ring of rings) {
    for (
      let index = 0, previous = ring.length - 2;
      index < ring.length;
      previous = index, index += 2
    ) {
      yield [
        [ring[previous] ?? 0, ring[previous + 1] ?? 0],
        [ring[index] ?? 0, ring[index + 1] ?? 0],
      ] as const;
    }
  }
}

interface ChinaMapOutlineIndex {
  bbox: ChinaMapBbox;
  children?: readonly [ChinaMapOutlineIndex, ChinaMapOutlineIndex];
  edges?: readonly (readonly [ChinaMapPoint, ChinaMapPoint])[];
}

/** Immutable outlines share an index across camera positions. Exact edge tests remain the final
 * authority; bounding boxes only skip segments that cannot possibly touch the candidate. */
const getOutlineIndex = memoize(
  (rings: readonly ChinaMapRing[]): ChinaMapOutlineIndex => {
    const build = (edges: (readonly [ChinaMapPoint, ChinaMapPoint])[]): ChinaMapOutlineIndex => {
      const bbox: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity];
      for (const [a, b] of edges) {
        bbox[0] = Math.min(bbox[0], a[0], b[0]);
        bbox[1] = Math.min(bbox[1], a[1], b[1]);
        bbox[2] = Math.max(bbox[2], a[0], b[0]);
        bbox[3] = Math.max(bbox[3], a[1], b[1]);
      }
      if (edges.length <= 8) {
        return { bbox, edges };
      }
      const axis = bbox[2] - bbox[0] >= bbox[3] - bbox[1] ? 0 : 1;
      edges.sort((a, b) => a[0][axis] + a[1][axis] - b[0][axis] - b[1][axis]);
      const middle = Math.floor(edges.length / 2);
      return { bbox, children: [build(edges.slice(0, middle)), build(edges.slice(middle))] };
    };
    return build([...getChinaMapRingEdges(rings)]);
  },
  { cache: new WeakMap() },
);

function someOutlineEdge(
  index: ChinaMapOutlineIndex,
  box: ChinaMapBbox,
  predicate: (a: ChinaMapPoint, b: ChinaMapPoint) => boolean,
): boolean {
  if (!doChinaMapBboxesIntersect(index.bbox, box)) {
    return false;
  }
  return index.children === undefined
    ? (index.edges?.some(([a, b]) => predicate(a, b)) ?? false)
    : someOutlineEdge(index.children[0], box, predicate) ||
        someOutlineEdge(index.children[1], box, predicate);
}

function isPointInOutlineIndex(index: ChinaMapOutlineIndex, x: number, y: number): boolean {
  let inside = false;
  someOutlineEdge(index, [x, y, Infinity, y], (a, b) => {
    if (a[1] > y !== b[1] > y && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0]) {
      inside = !inside;
    }
    return false;
  });
  return inside;
}

/** The L∞ distance from a point to the segment's point `t` of the way along it, if `t` is on it. */
function measureRoomAlongSegment(
  t: number,
  ux: number,
  uy: number,
  dx: number,
  dy: number,
): number {
  // Division by zero gives ±Infinity or NaN, which this range check rejects.
  return t > 0 && t < 1
    ? Math.max(Math.abs(ux + t * dx), Math.abs(uy + t * dy))
    : Number.POSITIVE_INFINITY;
}

/** The nearest outline edge's L∞ distance from a point, if nearer than `room`. */
function measureOutlineRoom(
  index: ChinaMapOutlineIndex,
  x: number,
  y: number,
  room: number,
): number {
  const gap = ({ bbox }: ChinaMapOutlineIndex) =>
    Math.max(bbox[0] - x, x - bbox[2], bbox[1] - y, y - bbox[3]);
  if (gap(index) >= room) {
    return room;
  }
  if (index.children === undefined) {
    let nearest = room;
    for (const [[ax, ay], [bx, by]] of index.edges ?? []) {
      // Along the edge, the distance is the larger of two linear functions' magnitudes, so its
      // minimum is at an end or where one of them is zero or they are equal.
      const ux = ax - x;
      const uy = ay - y;
      const dx = bx - ax;
      const dy = by - ay;
      nearest = Math.min(
        nearest,
        Math.max(Math.abs(ux), Math.abs(uy)),
        Math.max(Math.abs(bx - x), Math.abs(by - y)),
        measureRoomAlongSegment(-ux / dx, ux, uy, dx, dy),
        measureRoomAlongSegment(-uy / dy, ux, uy, dx, dy),
        measureRoomAlongSegment((uy - ux) / (dx - dy), ux, uy, dx, dy),
        measureRoomAlongSegment(-(ux + uy) / (dx + dy), ux, uy, dx, dy),
      );
    }
    return nearest;
  }
  // The nearer half first, so its edges can rule out the farther half.
  const [first, second] = index.children;
  const [near, far] = gap(first) <= gap(second) ? [first, second] : [second, first];
  return measureOutlineRoom(far, x, y, measureOutlineRoom(near, x, y, room));
}

/**
 * The room at a point: half the side of the largest square centered there that no outline (nor
 * hole) enters, i.e. the L∞ distance to the outline. Negative outside the region. A character fits
 * on a spot where the room is at least the half-side of its square. Edges farther than `limit` are
 * never searched, so a spot with more room than that measures `limit` (or `-limit` outside).
 */
export function measureChinaMapRingsRoom(
  rings: readonly ChinaMapRing[],
  x: number,
  y: number,
  limit = Number.POSITIVE_INFINITY,
): number {
  const index = getOutlineIndex(rings);
  const room = measureOutlineRoom(index, x, y, limit);
  return isPointInOutlineIndex(index, x, y) ? room : -room;
}

/** Intersects filled land even when a tiny island lies wholly inside the label's rectangle. */
export function doesChinaMapBboxIntersectRings(
  rings: readonly ChinaMapRing[],
  box: ChinaMapBbox,
): boolean {
  const index = getOutlineIndex(rings);
  return (
    someOutlineEdge(index, box, (start, end) =>
      doesChinaMapSegmentIntersectBbox(start, end, box),
    ) || isPointInOutlineIndex(index, (box[0] + box[2]) / 2, (box[1] + box[3]) / 2)
  );
}

export function doesChinaMapSegmentIntersectRings(
  rings: readonly ChinaMapRing[],
  start: ChinaMapPoint,
  end: ChinaMapPoint,
): boolean {
  const index = getOutlineIndex(rings);
  return (
    someOutlineEdge(
      index,
      [
        Math.min(start[0], end[0]),
        Math.min(start[1], end[1]),
        Math.max(start[0], end[0]),
        Math.max(start[1], end[1]),
      ],
      (a, b) => doChinaMapSegmentsIntersect(start, end, a, b),
    ) ||
    isPointInOutlineIndex(index, ...start) ||
    isPointInOutlineIndex(index, ...end)
  );
}

/**
 * A rectangle fits only if its center is land and no outline (including a hole) enters it. The
 * cached outline index only skips edges that cannot reach the rectangle.
 */
export function isChinaMapBboxInsideRings(
  rings: readonly ChinaMapRing[],
  box: readonly [number, number, number, number],
): boolean {
  const index = getOutlineIndex(rings);
  return (
    !someOutlineEdge(index, box, (start, end) =>
      doesChinaMapSegmentIntersectBbox(start, end, box),
    ) && isPointInOutlineIndex(index, (box[0] + box[2]) / 2, (box[1] + box[3]) / 2)
  );
}
